import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { Icons } from '../../../../shared/helpers/icons';
import { IMaterial } from '../../../../shared/models/materials';
import { MaterialEquipmentService } from '../../../../shared/services/materialsEquipment.service';
import { FormsModule } from '@angular/forms';
import { UserDataService } from '../../../../shared/services/userData.service';
import { ContactsService } from '../../../../shared/services/contact.service';
import { IContact } from '../../../../shared/models/contact';

@Component({
  selector: 'edit-materials-equipment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    SpinnerComponent
  ],
  templateUrl: './edit-materials-equipment.component.html',
  styleUrl: './edit-materials-equipment.component.css'
})
export class EditMaterialsEquipmentComponent implements OnInit {
  icons = Icons;
  currentBuildingId: string | null = null;
  fullContactsList: IContact[] = [];
  contactsList: IContact[] = [];

  @Input() materialToEdit: IMaterial | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  materialData: IMaterial = {
    material: '',
    contact_id: 0,
    location: ''
  };

  suggestions: string[] = [
    'Blankets',
    'Emergency Communication Devices (Radios, Cell, Bullhorn, etc.)',
    'Food',
    'Medical (AED)',
    'Medical (First Aid)',
    'Medical (Medication)',
    'Medical Supplies',
    'Recovery (Counseling/Mental Health)',
    'Tools',
    'Water'
  ];

  loading: boolean = false;
  showSuggestions: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private materialEquipmentService: MaterialEquipmentService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.loadContacts();
  }

  // ✅ react to changes in the input
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['materialToEdit'] && this.materialToEdit) {
      this.materialData = {
        material: this.materialToEdit.material || '',
        contact_id: this.materialToEdit.contact_id || 0,
        location: this.materialToEdit.location || ''
      };
    }
  }

  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        this.contactsList = [...this.fullContactsList].sort((a, b) => a.name.localeCompare(b.name));

        // if no contact set in material, default to first
        if (!this.materialData.contact_id && this.contactsList.length > 0) {
          this.materialData.contact_id = this.contactsList[0].id;
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  }

  get filteredSuggestions(): string[] {
    const query = this.materialData.material?.toLowerCase() || '';
    return this.suggestions.filter(s => s.toLowerCase().includes(query));
  }

  selectSuggestion(value: string): void {
    this.materialData.material = value;
    this.showSuggestions = false;
  }

  hideSuggestions(): void {
    setTimeout(() => this.showSuggestions = false, 150);
  }

  closeModal(): void {
    this.onClose.emit();
  }

  saveEdit(): void {
    if (this.materialToEdit && this.materialToEdit.id && this.currentBuildingId) {
      this.loading = true;
      console.log("Submitting edited material:", this.materialData);

      this.materialEquipmentService.updateMaterial(this.currentBuildingId, String(this.materialToEdit.id), this.materialData).subscribe({
        next: (res) => {
          this.loading = false;
          if (!res.has_error) {
            this.onSave.emit({ type: 'success', message: 'This occurred after updating a material.' });
            this.closeModal();
          }
        },
        error: (err) => {
          this.loading = false;
          this.onSave.emit({ type: 'unsuccess', message: err.message });
        }
      });
    }
  }
}
